import type {CmLogger} from "@cm/log"
// adapted from skilitics/two-fingers
//
// MIT License
//
// Copyright (c) 2020 Skilitics
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
//     The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
//     THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
//     FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
//     OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

export * from "../types/navigation"
import {Coords, Gesture, GestureCallbacks, NormalizedWheelEvent, TrackpadZoom} from "@app/node-editor/types/navigation"

const midpoint = (touchList: TouchList): Coords => ({
    x: (touchList[0].clientX + touchList[1].clientX) / 2,
    y: (touchList[0].clientY + touchList[1].clientY) / 2,
})

const distance = (touchList: TouchList) => {
    const dx = touchList[1].clientX - touchList[0].clientX
    const dy = touchList[1].clientY - touchList[0].clientY

    return Math.sqrt(dx * dx + dy * dy)
}

const angle = (touchList: TouchList) => {
    const dx = touchList[1].clientX - touchList[0].clientX
    const dy = touchList[1].clientY - touchList[0].clientY

    return (180 / Math.PI) * Math.atan2(dy, dx)
}

export const clientToHTMLElementCoords = (element: HTMLDivElement, coords: Coords): Coords => {
    const rect = element.getBoundingClientRect()

    return {
        x: coords.x - rect.x,
        y: coords.y - rect.y,
    }
}

export const clientToSVGElementCoords = (el: SVGSVGElement, coords: Coords): Coords | undefined => {
    const element: SVGSVGElement = el.ownerSVGElement ?? el
    const coordinateTransformMatrix = element.getScreenCTM()

    if (!coordinateTransformMatrix) return

    const screenToElement = coordinateTransformMatrix.inverse()
    const point = element.createSVGPoint()

    point.x = coords.x
    point.y = coords.y

    return point.matrixTransform(screenToElement)
}

function limit(delta: number, max_delta: number) {
    return Math.sign(delta) * Math.min(max_delta, Math.abs(delta))
}

function normalizeWheel(e: WheelEvent) {
    const DELTA_LINE_MULTIPLIER = 8
    const DELTA_PAGE_MULTIPLIER = 24
    const MAX_WHEEL_DELTA = 24

    let dx = e.deltaX
    let dy = e.deltaY

    if (e.shiftKey && dx === 0) {
        ;[dx, dy] = [dy, dx]
    }

    if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) {
        dx *= DELTA_LINE_MULTIPLIER
        dy *= DELTA_LINE_MULTIPLIER
    } else if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
        dx *= DELTA_PAGE_MULTIPLIER
        dy *= DELTA_PAGE_MULTIPLIER
    }
    return {
        dx: limit(dx, MAX_WHEEL_DELTA),
        dy: limit(dy, MAX_WHEEL_DELTA),
    }
}

export const trackpadZoom = (
    container: Element,
    {onGestureStart, onGestureChange, onGestureEnd}: GestureCallbacks = {},
    normalizeWheelEvent: (event: WheelEvent) => NormalizedWheelEvent = normalizeWheel,
): TrackpadZoom => {
    // TODO: we shouldn't be reusing gesture
    let gesture: Gesture | undefined = undefined
    let timer: number

    const wheelListener = (event: Event) => {
        if (!(event instanceof WheelEvent)) {
            trackpadLogger?.error("Expected WheelEvent, got", event)
            return
        }

        event.preventDefault()

        const {dx, dy} = normalizeWheelEvent(event)

        if (!gesture) {
            gesture = {
                scale: 1,
                translation: {x: 0, y: 0},
                origin: {x: event.clientX, y: event.clientY},
            }
            onGestureStart?.(gesture)
        } else {
            gesture = {
                origin: {x: event.clientX, y: event.clientY},
                scale: event.ctrlKey ? gesture.scale * (1 - dy / 100) : 1,
                translation: !event.ctrlKey
                    ? {
                          x: gesture.translation.x - dx,
                          y: gesture.translation.y - dy,
                      }
                    : {x: 0, y: 0},
            }

            onGestureChange?.(gesture)
        }

        if (timer) {
            window.clearTimeout(timer)
        }

        timer = window.setTimeout(() => {
            if (gesture) {
                onGestureEnd?.(gesture)
                gesture = undefined
            }
        }, 20)
    }

    let initialTouches: TouchList

    const touchMove = (e: Event) => {
        if (!(e instanceof TouchEvent)) return

        if (e.touches.length === 2) {
            const initialMidpoint = midpoint(initialTouches)
            const currentMidpoint = midpoint(e.touches)

            gesture = {
                scale: e.scale !== undefined ? e.scale : distance(e.touches) / distance(initialTouches),
                rotation: e.rotation !== undefined ? e.rotation : angle(e.touches) - angle(initialTouches),
                translation: {
                    x: currentMidpoint.x - initialMidpoint.x,
                    y: currentMidpoint.y - initialMidpoint.y,
                },
                origin: initialMidpoint,
            }

            onGestureChange?.(gesture)
            e.preventDefault()
        }
    }

    const watchTouches = (e: Event) => {
        if (!(e instanceof TouchEvent)) return

        if (e.touches.length === 2) {
            initialTouches = e.touches
            gesture = {
                scale: 1,
                rotation: 0,
                translation: {x: 0, y: 0},
                origin: midpoint(initialTouches),
            }

            /*
                      All the other events using `watchTouches` are passive,
                      we don't need to call preventDefault().
                   */
            if (e.type === "touchstart") {
                e.preventDefault()
            }

            onGestureStart?.(gesture)
            container.addEventListener("touchmove", touchMove, {passive: false})
            container.addEventListener("touchend", watchTouches)
            container.addEventListener("touchcancel", watchTouches)
        } else if (gesture) {
            onGestureEnd?.(gesture)
            gesture = undefined
            container.removeEventListener("touchmove", touchMove)
            container.removeEventListener("touchend", watchTouches)
            container.removeEventListener("touchcancel", watchTouches)
        }
    }

    container.addEventListener("touchstart", watchTouches, {passive: false})

    /*
      GestureEvent handling - Safari only
    */

    const handleGestureStart = ({clientX, clientY, rotation, scale, preventDefault}: GestureEvent) => {
        onGestureStart?.({
            translation: {x: 0, y: 0},
            scale,
            rotation,
            origin: {x: clientX, y: clientY},
        })

        try {
            preventDefault()
        } catch (error: unknown) {
            // Some browsers (e.g. Safari) do not support preventDefault on GestureEvent
            // This is a no-op, but we catch it to avoid errors in the console
        }
    }

    const handleGestureChange = ({clientX, clientY, rotation, scale, preventDefault}: GestureEvent) => {
        onGestureChange?.({
            translation: {x: 0, y: 0},
            scale,
            rotation,
            origin: {x: clientX, y: clientY},
        })

        try {
            preventDefault()
        } catch (error: unknown) {
            // Some browsers (e.g. Safari) do not support preventDefault on GestureEvent
            // This is a no-op, but we catch it to avoid errors in the console
        }
    }

    const handleGestureEnd = ({clientX, clientY, rotation, scale}: GestureEvent) => {
        onGestureEnd?.({
            translation: {x: 0, y: 0},
            scale,
            rotation,
            origin: {x: clientX, y: clientY},
        })
    }

    if (typeof window.GestureEvent !== "undefined" && typeof window.TouchEvent === "undefined") {
        container.addEventListener("gesturestart", handleGestureStart, {passive: false})
        container.addEventListener("gesturechange", handleGestureChange, {passive: false})
        container.addEventListener("gestureend", handleGestureEnd)
    }

    return {
        unregister: () => {
            container.removeEventListener("touchstart", watchTouches)

            if (typeof window.GestureEvent !== "undefined" && typeof window.TouchEvent === "undefined") {
                container.removeEventListener("gesturestart", handleGestureStart)
                container.removeEventListener("gesturechange", handleGestureChange)
                container.removeEventListener("gestureend", handleGestureEnd)
            }
        },
        wheelListener,
    }
}

export let trackpadLogger: CmLogger | undefined
